import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import {
    Box, Checkbox, DialogContent, DialogTitle,
    FormControlLabel, Icon, IconButton, InputBase,
    MenuItem, Paper, TextField, Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DisabledFormWrapper, CLUSTER_STATES } from 'app/main/util/clusterUtils';
import { RESOURCE_MANAGER } from '../../util/enums';
import { ipValid } from '../../util/InputValidate';
import { getRegionsGroupedByContinent, getRegionLabel, findRegionByCode, regionToLocation } from './maasRegions';

import * as formHelpers from './formHelpers';
import * as StackActions from 'app/main/stacks/store/actions/stacks.actions.js';
import * as Actions from '../store/actions';
import { createWindowsDesktopCluster, updateWindowsDesktopCluster, getMaasWindowsOptions } from '../store/actions/cluster.actions';
import * as ActionsDialog from 'app/store/actions';
import useClusterForm from '../hooks/useClusterForm';
import ClusterFormHeader from '../components/ClusterFormHeader';
import QuotaWarningBanner from '../components/QuotaWarningBanner';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import DescriptionSection from '../components/DescriptionSection';
import SectionCard from '../components/SectionCard';
import { inputGroupStyle } from '../components/formLayoutStyles';

const MaasCluster = (props) => {
    // These must be declared before useClusterForm because customValidation references them
    const [selectedPreconfiguredDevice, setSelectedPreconfiguredDevice] = useState(null);
    const isCommissioned = Boolean(selectedPreconfiguredDevice);

    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace,
        disableInputs, isFormValid, formInvalidReason, quotas,
        handleClose, handleSubmit: baseHandleSubmit, handleEdit: baseHandleEdit,
    } = useClusterForm({
        provider: 'maas', props, formHelpers,
        customValidation: (f) => {
            if (!f.displayName) return false;
            if (isCommissioned) return Boolean(selectedPreconfiguredDevice);
            if (f.manager === 'torque') {
                if (!f.moabHostname) return false;
                if (!f.moabIp || !ipValid(f.moabIp)) return false;
            }
            return Boolean(f.credentialId && f.serverInstanceId && f.serverImageName && f.serverMachine);
        },
        onInit: (dispatch) => {
            dispatch(StackActions.getAcmImages());
            dispatch(StackActions.getAcmShapes());
            dispatch(Actions.provider_os_types('maas'));
            dispatch(Actions.getPreconfiguredDevices());
        },
    });

    const pkgRef = useRef(null);

    // MaaS-specific Redux state
    const supportedOSs = useSelector(({ clusters }) => clusters.cluster.os_types) || [];
    const acmLayers = useSelector(({ stacks }) => stacks.stack.acm_images) || [];
    const acmShapes = useSelector(({ stacks }) => stacks.stack.acm_shapes) || [];
    const preconfiguredDevices = useSelector(({ clusters }) => clusters.cluster.preconfiguredDevices) || [];
    const credentials = useSelector(({ credentials }) => credentials.credential.list) || [];

    // MaaS-specific UI state
    const [selectedDeviceFamily, setSelectedDeviceFamily] = useState(null);
    const [selectedHeadNodeShape, setSelectedHeadNodeShape] = useState(null);
    const [selectedCommissionedComputeNodes, setSelectedCommissionedComputeNodes] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState(null);

    // Derived
    const headNodeImages = useMemo(() =>
        acmLayers.filter(l => l.status === 'available' && l.type === 'oddc_base' && l.image_type === 'server').map(l => ({ name: l.name })),
        [acmLayers]);
    const computeNodeImages = useMemo(() =>
        acmLayers.filter(l => l.status === 'available' && l.type === 'oddc_base' && l.image_type === 'node').map(l => ({ name: l.name })),
        [acmLayers]);
    const nodeShapes = useMemo(() =>
        (acmShapes || []).map(s => ({ name: s.description, instance_id: s.name, machines: s.machine_names, all_machines: s.all_machine_names || [] })),
        [acmShapes]);
    const filteredCredentials = useMemo(() => credentials.filter(c => c.provider === 'maas'), [credentials]);
    const regionOptions = useMemo(() => getRegionsGroupedByContinent(), []);

    // Feature: windows-desktop-vms on MaaS - "Windows Desktop" is an entry in
    // the same OS Type picker (Standard MaaS deploy only), like the Azure form.
    // It deploys one Ready MaaS machine with a windows/* boot resource through
    // its own endpoint (commands/cluster/maas-windows/), so once selected it
    // swaps the Linux sections for a minimal set of fields and saves through
    // createWindowsDesktopCluster instead of saveCluster.
    const dispatch = useDispatch();
    const isWindowsDesktop = !isCommissioned && form?.osType === 'windows-desktop';
    // Same rule as the Azure Windows Desktop: editable only while still staged.
    const isWindowsEdit = isWindowsDesktop && props.type === 'edit' && form?.state !== CLUSTER_STATES.DESTROYED;
    const [windowsSubmitting, setWindowsSubmitting] = useState(false);
    const [windowsError, setWindowsError] = useState(null);
    const [windowsOptions, setWindowsOptions] = useState({ machines: [], images: [], loading: false, error: null });

    useEffect(() => {
        if (!isWindowsDesktop || !form?.credentialId) {
            setWindowsOptions({ machines: [], images: [], loading: false, error: null });
            return undefined;
        }
        let cancelled = false;
        setWindowsOptions((o) => ({ ...o, loading: true, error: null }));
        dispatch(getMaasWindowsOptions(form.credentialId))
            .then((data) => {
                if (!cancelled) setWindowsOptions({ machines: data.machines || [], images: data.images || [], loading: false, error: null });
            })
            .catch((err) => {
                if (cancelled) return;
                const message = (err.response && err.response.data && err.response.data.message) || err.message;
                setWindowsOptions({ machines: [], images: [], loading: false, error: message });
            });
        return () => { cancelled = true; };
    }, [dispatch, isWindowsDesktop, form?.credentialId]);

    // A staged cluster's machine may no longer be Ready (deployed by something
    // else since) - keep showing it so the saved choice is visible.
    const windowsMachines = useMemo(() => {
        const list = windowsOptions.machines;
        if (form?.serverMachine && !list.some((m) => m.hostname === form.serverMachine)) {
            return [{ hostname: form.serverMachine, notReady: true }, ...list];
        }
        return list;
    }, [windowsOptions.machines, form?.serverMachine]);

    const windowsMissing = useMemo(() => {
        const missing = [];
        if (!form?.displayName) missing.push('cluster name');
        if (!form?.credentialId) missing.push('credential');
        if (!form?.serverMachine) missing.push('machine');
        if (!form?.serverImageName) missing.push('Windows image');
        return missing;
    }, [form?.displayName, form?.credentialId, form?.serverMachine, form?.serverImageName]);
    const windowsFormValid = windowsMissing.length === 0 && !windowsSubmitting;
    const windowsInvalidText = isWindowsEdit
        ? 'Windows Desktop clusters cannot be edited - destroy and recreate instead.'
        : (windowsError
            || (windowsMissing.length ? `Complete the following before saving: ${windowsMissing.join(', ')}.` : undefined));

    const handleWindowsSubmit = useCallback(async () => {
        setWindowsSubmitting(true);
        setWindowsError(null);
        try {
            const payload = {
                provider: 'maas',
                displayName: form.displayName,
                credentialId: form.credentialId,
                machine: form.serverMachine,
                imageName: form.serverImageName,
                enableRdsh: !!form.enableRdsh,
            };
            if (props.type === 'edit' && form.id) {
                await dispatch(updateWindowsDesktopCluster(form.id, payload));
            } else {
                await dispatch(createWindowsDesktopCluster(payload));
            }
            dispatch(ActionsDialog.closeDialog());
        } catch (err) {
            setWindowsError((err.response && err.response.data && err.response.data.message) || err.message || `Failed to ${props.type === 'edit' ? 'update' : 'create'} the cluster`);
        } finally {
            setWindowsSubmitting(false);
        }
    }, [dispatch, form, props.type]);

    // Switching into or out of Windows Desktop clears the head node picks: an
    // ACM layer is not a Windows image and an ACM shape is not a machine.
    const handleOsTypeChange = useCallback((value) => {
        const crossing = (value === 'windows-desktop') !== (form?.osType === 'windows-desktop');
        if (crossing) {
            setField({ osType: value, serverImageName: '', serverMachine: '', serverInstanceId: '' });
            setSelectedHeadNodeShape(null);
        } else {
            setField('osType', value);
        }
    }, [form?.osType, setField]);

    // Sync commissioned data to form so toApiShape picks it up on save.
    // Mongoose requires server.instance_id/image_name, ssh_username and node.group[].instance_id —
    // populating them from the selected device avoids a validation error on save for commissioned clusters.
    useEffect(() => {
        if (!form) return;
        if (isCommissioned && selectedPreconfiguredDevice && selectedDeviceFamily) {
            // osType resolution priority:
            //   1. Per-machine detected osType (commissioned-time value from
            //      commissioningService normalizeOsType, e.g. ubuntu-26.04 on
            //      the GB10 Sparks). Authoritative — what's actually on the box.
            //   2. Family-level hint (os_type on the device family). Used as
            //      seed before any machine is selected.
            //   3. Hard fallback to ubuntu-22.04 only if nothing is known.
            // Without (1), every commissioned cluster picked the family fallback
            // (hardcoded ubuntu-22.04 in the backend) and Heidi Desktop never
            // surfaced on Ubuntu 26 commissioned machines because the UI gates
            // on cluster.info['os-type'] startsWith 'ubuntu-26'.
            const resolvedOsType = selectedPreconfiguredDevice.osType
                || selectedDeviceFamily.os_type
                || 'ubuntu-22.04';
            setField({
                deviceType: 'commissioned',
                commissionedMachineId: selectedPreconfiguredDevice.id || null,
                commissionedComputeNodes: selectedCommissionedComputeNodes,
                location: regionToLocation(selectedRegion),
                serverInstanceId: selectedPreconfiguredDevice.instance_id || '',
                serverImageName: selectedDeviceFamily.machine_type,
                serverMachine: selectedDeviceFamily.machine_type,
                osType: resolvedOsType,
                nodeGroups: [],
            });
        } else {
            setField({
                deviceType: 'standard',
                commissionedMachineId: null,
                commissionedComputeNodes: [],
                location: regionToLocation(selectedRegion),
            });
        }
    }, [isCommissioned, selectedPreconfiguredDevice, selectedDeviceFamily, selectedCommissionedComputeNodes, selectedRegion]);

    // Restore device family + commissioned machine on edit
    useEffect(() => {
        if (!form?.serverMachine || !preconfiguredDevices.length) return;
        const family = preconfiguredDevices.find(df => df.machine_type === form.serverMachine);
        if (family) {
            setSelectedDeviceFamily(family);
            if (form.commissionedMachineId) {
                const machineId = String(form.commissionedMachineId?.$oid || form.commissionedMachineId);
                for (const f of preconfiguredDevices) {
                    const match = f.devices?.find(d => String(d.id || d._id) === machineId);
                    if (match) { setSelectedPreconfiguredDevice(match); break; }
                }
            }
            if (form.commissionedComputeNodes?.length > 0) {
                setSelectedCommissionedComputeNodes(form.commissionedComputeNodes.map(id => String(id?.$oid || id)));
            }
        }
    }, [form?.serverMachine, form?.commissionedMachineId, preconfiguredDevices]);

    // Restore region on edit
    useEffect(() => {
        if (props.type === 'edit' && form?.location?.region && !selectedRegion) {
            setSelectedRegion(findRegionByCode(form.location.region) || null);
        }
    }, [props.type, form?.location, selectedRegion]);

    // Pre-select head node shape on edit
    useEffect(() => {
        if (form?.serverInstanceId && nodeShapes.length > 0 && !selectedHeadNodeShape) {
            const shape = nodeShapes.find(s => s.instance_id === form.serverInstanceId);
            if (shape) setSelectedHeadNodeShape(shape);
        }
    }, [form?.serverInstanceId, nodeShapes, selectedHeadNodeShape]);

    const handleHeadNodeShapeChange = useCallback((event, newValue) => {
        setSelectedHeadNodeShape(newValue);
        setField('serverInstanceId', newValue?.instance_id || '');
    }, [setField]);

    const getAvailableMachines = useCallback(() =>
        nodeShapes.filter(shape => shape.machines?.length > 0), [nodeShapes]);

    const addPackage = useCallback(() => {
        if (!pkgRef.current?.value) return;
        setField('packages', [...(form?.packages || []), pkgRef.current.value]);
        pkgRef.current.value = '';
    }, [form?.packages, setField]);

    const removePackage = useCallback((pkg) => {
        setField('packages', (form?.packages || []).filter(p => p !== pkg));
    }, [form?.packages, setField]);

    const handleDeviceFamilyChange = useCallback((newDeviceType) => {
        setSelectedCommissionedComputeNodes([]);
        if (newDeviceType !== 'standard') {
            const family = preconfiguredDevices.find(df => df.machine_type === newDeviceType);
            setSelectedDeviceFamily(family);
            setField('serverMachine', newDeviceType);
            if (family?.devices?.length > 0) setSelectedPreconfiguredDevice(family.devices[0]);
            else setSelectedPreconfiguredDevice(null);
        } else {
            setSelectedDeviceFamily(null);
            setSelectedPreconfiguredDevice(null);
            setField('serverMachine', '');
        }
    }, [preconfiguredDevices, setField]);

    // MaaS header — no marketplace, use form name directly
    const maasMarketplace = {
        ...marketplace,
        title: form?.hostname || form?.displayName || 'New Cluster',
        imageSrc: 'assets/images/providers/acm.png',
    };

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="maas" providerLabel="ACM" marketplace={maasMarketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isWindowsDesktop ? (isWindowsEdit ? false : windowsFormValid) : isFormValid}
                            formInvalidReason={isWindowsDesktop ? windowsInvalidText : formInvalidReason}
                            loading={loading || windowsSubmitting} disableInputs={disableInputs}
                            formType={props.type}
                            onSave={isWindowsDesktop ? handleWindowsSubmit : baseHandleSubmit}
                            onEdit={isWindowsDesktop ? (isWindowsEdit ? undefined : handleWindowsSubmit) : baseHandleEdit}
                            onClose={handleClose}
                        />
                    </DialogTitle>

                    <QuotaWarningBanner quotas={quotas} />

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs || isWindowsEdit}>
                            <div className="!w-full">

                                {/* General Section */}
                                <SectionCard icon="info" title="General">
                                    <div style={inputGroupStyle}>
                                        <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                            label="Cluster Name" variant="outlined" required
                                            value={form.displayName || ''} onChange={(e) => setField('displayName', e.target.value)} />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                            label="Hostname" variant="outlined"
                                            value={form.hostname || ''} onChange={(e) => setField('hostname', e.target.value)}
                                            disabled={props.type !== 'edit'}
                                            placeholder={props.type !== 'edit' ? 'Auto-generated on save' : ''} />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                            label="Device Family *" variant="outlined" required
                                            value={isCommissioned ? selectedDeviceFamily?.machine_type || '' : 'standard'}
                                            onChange={(e) => handleDeviceFamilyChange(e.target.value)}
                                            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
                                            <MenuItem value="standard">Standard (MaaS Deploy)</MenuItem>
                                            {preconfiguredDevices.map(f => (
                                                <MenuItem key={f.machine_type} value={f.machine_type}>{f.name} (Commissioned)</MenuItem>
                                            ))}
                                        </TextField>
                                        {!isCommissioned && (
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="OS Type" variant="outlined" required
                                                value={form.osType || ''} onChange={(e) => handleOsTypeChange(e.target.value)}
                                                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
                                                {supportedOSs.map((os, i) => <MenuItem key={i} value={os}>{os}</MenuItem>)}
                                                <MenuItem value="windows-desktop">Windows Desktop</MenuItem>
                                            </TextField>
                                        )}
                                        {!isCommissioned && (
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Credential *" variant="outlined" required
                                                value={form.credentialId || ''} onChange={(e) => setField('credentialId', e.target.value)}
                                                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
                                                {filteredCredentials.map((c, i) => <MenuItem key={i} value={c.id}>{c.name}</MenuItem>)}
                                            </TextField>
                                        )}
                                        {isCommissioned && selectedDeviceFamily?.devices?.length > 0 && (
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Head Node Device *" variant="outlined"
                                                value={selectedPreconfiguredDevice?.id || ''}
                                                onChange={(e) => {
                                                    const device = selectedDeviceFamily.devices.find(d => (d.id || d._id) === e.target.value);
                                                    setSelectedPreconfiguredDevice(device || null);
                                                }}
                                                helperText={selectedPreconfiguredDevice && (selectedPreconfiguredDevice.osType
                                                    ? 'Detected OS: ' + selectedPreconfiguredDevice.osType
                                                    : 'OS not yet detected on this machine; cluster will fall back to ' + (selectedDeviceFamily.os_type || 'ubuntu-22.04'))}>
                                                {selectedDeviceFamily.devices.map(d => (
                                                    <MenuItem key={d.id || d._id} value={d.id || d._id}>{d.name} ({d.ipAddress || d.public_ip})</MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    </div>
                                    {!isWindowsDesktop && (
                                    <div style={inputGroupStyle}>
                                        <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                            label="Manager" variant="outlined" required
                                            value={form.manager || 'none'} onChange={(e) => setField('manager', e.target.value)}>
                                            <MenuItem value="none">None</MenuItem>
                                            <MenuItem value="slurm">Slurm</MenuItem>
                                            {!isCommissioned && <MenuItem value="torque">Torque</MenuItem>}
                                        </TextField>
                                    </div>
                                    )}
                                    {!isWindowsDesktop && form.manager === RESOURCE_MANAGER.TORQUE && (
                                        <div style={inputGroupStyle}>
                                            <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Moab Hostname" variant="outlined" required
                                                value={form.moabHostname || ''} onChange={(e) => setField('moabHostname', e.target.value)} />
                                            <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Moab IP" variant="outlined" required
                                                value={form.moabIp || ''} onChange={(e) => setField('moabIp', e.target.value)}
                                                error={Boolean(form.moabIp && !ipValid(form.moabIp))}
                                                helperText={form.moabIp && !ipValid(form.moabIp) ? 'Not a valid IP' : ''} />
                                        </div>
                                    )}
                                    {!isWindowsDesktop && (
                                    <Autocomplete
                                        options={regionOptions} groupBy={(o) => o.continent}
                                        getOptionLabel={(o) => getRegionLabel(o)}
                                        value={selectedRegion}
                                        onChange={(e, v) => setSelectedRegion(v)}
                                        renderInput={(params) => <TextField {...params} label="Geographic Location" variant="outlined" placeholder="Select region" />}
                                        style={{ marginBottom: '16px' }}
                                    />
                                    )}
                                </SectionCard>


                                {isWindowsDesktop && (
                                    <SectionCard icon="desktop_windows" title="Windows Desktop (MaaS)">
                                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                                            {isWindowsEdit
                                                ? 'This cluster’s settings cannot be changed after creation. Destroy it and create a new one to change the credential, machine or image.'
                                                : 'MaaS installs Windows on one Ready machine, reached in the browser over RDP through Guacamole. Enable multi-session below for one server shared by several students (RDS).'}
                                        </Typography>
                                        {windowsOptions.error && (
                                            <Typography variant="body2" style={{ color: '#d32f2f', marginBottom: 16 }}>
                                                {windowsOptions.error}
                                            </Typography>
                                        )}
                                        <div style={inputGroupStyle}>
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Machine *" variant="outlined" required
                                                value={form.serverMachine || ''}
                                                onChange={(e) => setField({ serverMachine: e.target.value, serverInstanceId: e.target.value })}
                                                disabled={!form.credentialId || windowsOptions.loading}
                                                helperText={!form.credentialId
                                                    ? 'Select a Credential first'
                                                    : (windowsOptions.loading
                                                        ? 'Reading machines from MaaS…'
                                                        : (!windowsOptions.error && windowsMachines.length === 0 ? 'MaaS has no Ready machine' : 'Ready machines in MaaS'))}
                                                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
                                                {windowsMachines.map((m) => (
                                                    <MenuItem key={m.hostname} value={m.hostname}>
                                                        {m.notReady
                                                            ? `${m.hostname} (not Ready now)`
                                                            : `${m.hostname} - ${m.cpuCount} CPU, ${Math.round((m.memoryMb || 0) / 1024)} GB${m.pool ? `, pool ${m.pool}` : ''}`}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Windows Image *" variant="outlined" required
                                                value={form.serverImageName || ''}
                                                onChange={(e) => setField('serverImageName', e.target.value)}
                                                disabled={!form.credentialId || windowsOptions.loading}
                                                helperText={form.credentialId && !windowsOptions.loading && !windowsOptions.error && windowsOptions.images.length === 0
                                                    ? 'No windows/* image uploaded to this MaaS'
                                                    : 'Windows boot resources in MaaS'}
                                                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
                                                {form.serverImageName && !windowsOptions.images.some((i) => i.name === form.serverImageName) && (
                                                    <MenuItem value={form.serverImageName}>{form.serverImageName}</MenuItem>
                                                )}
                                                {windowsOptions.images.map((i) => (
                                                    <MenuItem key={i.name} value={i.name}>{i.title} ({i.name})</MenuItem>
                                                ))}
                                            </TextField>
                                        </div>
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    checked={!!form.enableRdsh}
                                                    onChange={(e) => setField('enableRdsh', e.target.checked)}
                                                />
                                            )}
                                            label="Enable multi-session (several students, one server)"
                                        />
                                        <Typography variant="caption" color="textSecondary" component="div">
                                            Multi-session needs RDS licences on the Windows server. Without it Windows allows 2 users at a time.
                                        </Typography>
                                    </SectionCard>
                                )}

                                {/* Head Node — standard only */}
                                {!isCommissioned && !isWindowsDesktop && (
                                    <SectionCard icon="dns" title="Head Node">
                                        <div style={inputGroupStyle}>
                                            <Autocomplete className={clsx(classes.input)} style={{ flex: 1 }}
                                                options={headNodeImages} getOptionLabel={(o) => o.name}
                                                value={form.serverImageName ? { name: form.serverImageName } : null}
                                                onChange={(e, v) => setField('serverImageName', v?.name || '')}
                                                renderInput={(params) => <TextField {...params} label="Head Node Image" variant="outlined" required />}
                                            />
                                            <Autocomplete style={{ width: '33%' }}
                                                options={getAvailableMachines()} getOptionLabel={(o) => o.name}
                                                value={nodeShapes.find(s => s.instance_id === form.serverInstanceId) || null}
                                                onChange={handleHeadNodeShapeChange}
                                                renderInput={(params) => <TextField {...params} label="Head Node Size *" variant="outlined" />}
                                            />
                                        </div>
                                        {selectedHeadNodeShape?.all_machines?.length > 0 && (
                                            <div>
                                                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '8px' }}>Select Machine</Typography>
                                                <div className="flex space-x-6">
                                                    {selectedHeadNodeShape.all_machines.map(machine => (
                                                        <FormControlLabel key={machine}
                                                            control={<input type="radio" name="machine" checked={form.serverMachine === machine}
                                                                onChange={() => {
                                                                    setField('serverMachine', machine);
                                                                    const updatedGroups = (form.nodeGroups || []).map(g => ({
                                                                        ...g, machines: (g.machines || []).filter(m => m !== machine),
                                                                        count: (g.machines || []).filter(m => m !== machine).length,
                                                                    }));
                                                                    setField('nodeGroups', updatedGroups);
                                                                }} className="mr-2" />}
                                                            label={machine}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </SectionCard>
                                )}

                                {/* Compute Nodes — standard */}
                                {!isCommissioned && !isWindowsDesktop && (
                                    <SectionCard icon="memory" title="Compute Nodes"
                                        action={<button onClick={() => {
                                            const groups = form.nodeGroups || [];
                                            if (groups.length < 250) setField('nodeGroups', [...groups, { ...groups[0], count: 1, group_node_prefix_label: `Node Group ${groups.length + 1}` }]);
                                        }} className={classes.grayButton} style={{ padding: '4px 12px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px', background: '#fafafa' }}>+ Add Node Group</button>}>
                                        {(form.nodeGroups || []).map((group, i) => (
                                            <Box key={`node-${i}`} mb={2} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                                <div style={inputGroupStyle}>
                                                    <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                        label="Label" variant="outlined" required
                                                        value={group.group_node_prefix_label || ''}
                                                        onChange={(e) => { const u = [...(form.nodeGroups || [])]; u[i] = { ...u[i], group_node_prefix_label: e.target.value }; setField('nodeGroups', u); }} />
                                                    <Autocomplete className={clsx(classes.input)} style={{ flex: 1 }}
                                                        options={computeNodeImages} getOptionLabel={(o) => o.name}
                                                        value={group.group_node_prefix ? { name: group.group_node_prefix } : null}
                                                        onChange={(e, v) => { const u = [...(form.nodeGroups || [])]; u[i] = { ...u[i], group_node_prefix: v?.name || '' }; setField('nodeGroups', u); }}
                                                        renderInput={(params) => <TextField {...params} label="Compute Node Image" variant="outlined" required />} />
                                                    <Autocomplete style={{ width: '25%' }}
                                                        options={getAvailableMachines()} getOptionLabel={(o) => o.name}
                                                        getOptionSelected={(o, v) => o.instance_id === v.instance_id}
                                                        value={nodeShapes.find(s => s.instance_id === group.instance_id) || null}
                                                        onChange={(e, v) => { const u = [...(form.nodeGroups || [])]; u[i] = { ...u[i], instance_id: v?.instance_id || '', selectedNodeShape: v }; setField('nodeGroups', u); }}
                                                        renderInput={(params) => <TextField {...params} label="Size *" variant="outlined" />} />
                                                    <TextField style={{ width: '100px' }} label="Count" type="number" variant="outlined" required
                                                        value={group.machines?.length || group.count || 0}
                                                        onChange={(e) => { const u = [...(form.nodeGroups || [])]; u[i] = { ...u[i], count: parseInt(e.target.value, 10) || 0 }; setField('nodeGroups', u); }}
                                                        inputProps={{ min: 0, max: 250 }} />
                                                    {(form.nodeGroups || []).length > 1 && (
                                                        <IconButton size="small" onClick={() => setField('nodeGroups', (form.nodeGroups || []).filter((_, idx) => idx !== i))}>
                                                            <Icon>delete</Icon>
                                                        </IconButton>
                                                    )}
                                                </div>
                                            </Box>
                                        ))}
                                    </SectionCard>
                                )}

                                {/* Commissioned Compute Nodes */}
                                {isCommissioned && (
                                    <SectionCard icon="memory" title="Commissioned Compute Nodes">
                                        {selectedDeviceFamily?.devices?.length > 1 ? (
                                            <div>
                                                <Typography variant="body2" style={{ marginBottom: '16px', color: '#666' }}>
                                                    Select additional commissioned machines to use as compute nodes.
                                                </Typography>
                                                {selectedDeviceFamily.devices
                                                    .filter(d => (d.id || d._id) !== (selectedPreconfiguredDevice?.id || selectedPreconfiguredDevice?._id))
                                                    .map(device => {
                                                        const deviceId = device.id || device._id;
                                                        const isSelected = selectedCommissionedComputeNodes.includes(deviceId);
                                                        return (
                                                            <Box key={deviceId} display="flex" alignItems="center" mb={1} p={1}
                                                                style={{ backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5', borderRadius: '4px', border: isSelected ? '1px solid #2196F3' : '1px solid #e0e0e0' }}>
                                                                <Checkbox checked={isSelected}
                                                                    onChange={(e) => setSelectedCommissionedComputeNodes(prev => e.target.checked ? [...prev, deviceId] : prev.filter(id => id !== deviceId))}
                                                                    color="primary" disabled={disableInputs} />
                                                                <Box ml={1}>
                                                                    <Typography variant="body1" style={{ fontWeight: 500 }}>{device.name}</Typography>
                                                                    <Typography variant="body2" style={{ color: '#666' }}>{device.ipAddress || device.public_ip} • {device.architecture || 'auto'}</Typography>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                {selectedCommissionedComputeNodes.length > 0 && (
                                                    <Typography variant="body2" style={{ marginTop: '12px', color: '#4CAF50' }}>
                                                        {selectedCommissionedComputeNodes.length} compute node(s) selected
                                                    </Typography>
                                                )}
                                            </div>
                                        ) : (
                                            <Typography style={{ color: '#666' }}>
                                                {selectedDeviceFamily?.devices?.length === 1
                                                    ? 'No additional machines available.'
                                                    : 'Select a device family and head node first.'}
                                            </Typography>
                                        )}
                                    </SectionCard>
                                )}

                                {!isWindowsDesktop && (
                                    <AdvancedSectionsBlock
                                        form={form} setForm={setForm} setField={setField}
                                        provider="maas" advanced={advanced} marketplace={marketplace} disableInputs={disableInputs}
                                    />
                                )}

                                {/* Advanced: Volume + Network + Packages */}
                                {advanced && !isWindowsDesktop && (
                                    <SectionCard icon="settings" title="Advanced Settings">
                                        {!isCommissioned && (
                                            <div style={inputGroupStyle}>
                                                <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                    label="Server Volume (GB)" variant="outlined"
                                                    value={form.serverVolumeSize || ''} onChange={(e) => setField('serverVolumeSize', e.target.value)} />
                                                <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                    label="Node Volume (GB)" variant="outlined"
                                                    value={form.nodeVolumeSize || ''} onChange={(e) => setField('nodeVolumeSize', e.target.value)} />
                                            </div>
                                        )}
                                        <div style={inputGroupStyle}>
                                            <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Public IP" variant="outlined"
                                                value={form.publicIp || ''} onChange={(e) => setField('publicIp', e.target.value)} />
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Share" variant="outlined"
                                                value={form.share || 'nfs'} onChange={(e) => setField('share', e.target.value)}>
                                                <MenuItem value="nfs">NFS</MenuItem>
                                                <MenuItem value="beegfs">BeeGFS</MenuItem>
                                            </TextField>
                                        </div>
                                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '8px', marginTop: '16px' }}>Packages</Typography>
                                        <Paper style={{ display: 'flex', alignItems: 'center', padding: '2px 4px', marginBottom: '16px' }}>
                                            <InputBase inputRef={pkgRef} style={{ flex: 1, marginLeft: '8px' }} placeholder="Add a package" />
                                            <IconButton onClick={addPackage}><Icon>add</Icon></IconButton>
                                        </Paper>
                                        {(form.packages || []).map((pkg, i) => (
                                            <Box key={i} display="flex" alignItems="center" mb={0.5}>
                                                <Typography variant="body2" style={{ flex: 1 }}>{pkg}</Typography>
                                                <IconButton size="small" onClick={() => removePackage(pkg)}><Icon fontSize="small">delete</Icon></IconButton>
                                            </Box>
                                        ))}
                                    </SectionCard>
                                )}

                                {!isWindowsDesktop && <DescriptionSection form={form} setField={setField} />}
                            </div>
                        </DisabledFormWrapper>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default MaasCluster;
